import React, { useState, useEffect } from 'react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Settings, Anchor, Sailboat, Shield, Zap, Droplets } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useBateaux } from '@/hooks/useBateaux';
import { Maintenance } from '@/hooks/useMaintenances';

interface FormulaireMaintenanceProps {
  onComplete: () => void;
  maintenanceToEdit?: Maintenance;
  ajouterMaintenance: (maintenance: Omit<Maintenance, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => Promise<any>;
  modifierMaintenance: (id: string, modifications: Partial<Maintenance>) => Promise<any>;
  onSaved: () => Promise<void>;
}

const categories = [
  { value: 'Moteur', label: 'Moteur', icon: Settings },
  { value: 'Coque', label: 'Coque', icon: Anchor },
  { value: 'Voiles', label: 'Voiles', icon: Sailboat },
  { value: 'Équipements de sécurité', label: 'Équipements de sécurité', icon: Shield },
  { value: 'Électricité', label: 'Électricité', icon: Zap },
  { value: 'Plomberie', label: 'Plomberie', icon: Droplets },
];

const recurrences = [
  'Aucune',
  'Mensuelle',
  'Tous les 3 mois',
  'Tous les 6 mois',
  'Annuelle',
  'Tous les 100 heures'
];

const statuts = [
  'En attente',
  'En cours',
  'Terminée'
];

const FormulaireMaintenance = ({
  onComplete,
  maintenanceToEdit,
  ajouterMaintenance,
  modifierMaintenance,
  onSaved,
}: FormulaireMaintenanceProps) => {
  const { bateaux, loading: loadingBateaux } = useBateaux();
  
  const [formData, setFormData] = useState({
    bateau_id: '',
    titre: '',
    categorie: '',
    date_echeance: '',
    recurrence: 'Aucune',
    prix: '',
    reference_articles: '',
    description: '',
    statut: 'En attente',
    notifications_enabled: true
  });

  const [date, setDate] = useState<Date | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Réinitialiser le formulaire quand maintenanceToEdit change
  useEffect(() => {
    if (maintenanceToEdit) {
      console.log('Loading maintenance for edit:', maintenanceToEdit);
      
      // Parse la date correctement avec parseISO pour éviter les problèmes de timezone
      let parsedDate: Date | undefined = undefined;
      if (maintenanceToEdit.date_echeance) {
        try {
          parsedDate = parseISO(maintenanceToEdit.date_echeance);
          // Vérifier que la date est valide
          if (isNaN(parsedDate.getTime())) {
            console.error('Invalid date parsed:', maintenanceToEdit.date_echeance);
            parsedDate = undefined;
          }
        } catch (e) {
          console.error('Error parsing date:', e);
        }
      }
      
      setFormData({
        bateau_id: maintenanceToEdit.bateau_id || '',
        titre: maintenanceToEdit.titre || '',
        categorie: maintenanceToEdit.categorie || '',
        date_echeance: maintenanceToEdit.date_echeance || '',
        recurrence: maintenanceToEdit.recurrence || 'Aucune',
        prix: maintenanceToEdit.prix?.toString() || '',
        reference_articles: maintenanceToEdit.reference_articles || '',
        description: maintenanceToEdit.description || '',
        statut: maintenanceToEdit.statut || 'En attente',
        notifications_enabled: maintenanceToEdit.notifications_enabled ?? true
      });
      setDate(parsedDate);
      
      console.log('Form data set, date:', parsedDate);
    } else {
      // Réinitialiser pour un nouvel ajout
      setFormData({
        bateau_id: '',
        titre: '',
        categorie: '',
        date_echeance: '',
        recurrence: 'Aucune',
        prix: '',
        reference_articles: '',
        description: '',
        statut: 'En attente',
        notifications_enabled: true
      });
      setDate(undefined);
    }
  }, [maintenanceToEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.bateau_id || !formData.titre || !formData.categorie || !date) {
      return;
    }

    setIsSubmitting(true);

    try {
      const maintenanceData = {
        bateau_id: formData.bateau_id,
        titre: formData.titre,
        categorie: formData.categorie,
        date_echeance: format(date, 'yyyy-MM-dd'),
        recurrence: formData.recurrence,
        prix: formData.prix ? parseFloat(formData.prix) : undefined,
        reference_articles: formData.reference_articles || undefined,
        description: formData.description || undefined,
        statut: formData.statut,
        notifications_enabled: formData.notifications_enabled
      };

      if (maintenanceToEdit) {
        const result = await modifierMaintenance(maintenanceToEdit.id, maintenanceData);
        if (result?.error) {
          console.error('Erreur modification:', result.error);
          toast.error("Impossible de modifier la maintenance. Veuillez réessayer.");
          return;
        }
        toast.success("Maintenance modifiée avec succès");
      } else {
        const result = await ajouterMaintenance(maintenanceData);
        if (result?.error) {
          console.error('Erreur ajout:', result.error);
          toast.error("Impossible d'ajouter la maintenance. Veuillez réessayer.");
          return;
        }
        toast.success("Maintenance ajoutée avec succès");
      }

      // Rafraîchir la liste et fermer le dialog
      await onSaved();
      onComplete();
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingBateaux) {
    return <div className="text-center py-4 text-muted-foreground">Chargement des navires...</div>;
  }

  const isDisabled = !formData.bateau_id || !formData.titre || !formData.categorie || !date || isSubmitting;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label htmlFor="bateau">Navire *</Label>
          <Select 
            value={formData.bateau_id} 
            onValueChange={(value) => setFormData(prev => ({ ...prev, bateau_id: value }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Choisir un bateau" />
            </SelectTrigger>
            <SelectContent className="z-[100]">
              {bateaux.map((bateau) => (
                <SelectItem key={bateau.id} value={bateau.id}>
                  <div className="flex flex-col">
                    <span className="font-medium">{bateau.nom}</span>
                    <span className="text-sm text-muted-foreground">
                      {bateau.nom_constructeur} {bateau.nom_modele}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="titre">Titre de la maintenance *</Label>
          <Input
            id="titre"
            value={formData.titre}
            onChange={(e) => setFormData(prev => ({ ...prev, titre: e.target.value }))}
            placeholder="Ex: Vidange moteur"
            required
          />
        </div>

        <div>
          <Label htmlFor="categorie">Catégorie *</Label>
          <Select 
            value={formData.categorie} 
            onValueChange={(value) => setFormData(prev => ({ ...prev, categorie: value }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Choisir une catégorie" />
            </SelectTrigger>
            <SelectContent className="z-[100]">
              {categories.map((cat) => {
                const IconComponent = cat.icon;
                return (
                  <SelectItem key={cat.value} value={cat.value}>
                    <div className="flex items-center gap-2">
                      <IconComponent className="h-4 w-4" />
                      {cat.label}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Date d'échéance *</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !date && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(date, "dd/MM/yyyy", { locale: fr }) : <span>Sélectionner une date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 z-[100]" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                initialFocus
                className="pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
        </div>

        <div>
          <Label htmlFor="recurrence">Récurrence</Label>
          <Select 
            value={formData.recurrence} 
            onValueChange={(value) => setFormData(prev => ({ ...prev, recurrence: value }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[100]">
              {recurrences.map((rec) => (
                <SelectItem key={rec} value={rec}>{rec}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="prix">Prix (€)</Label>
          <Input
            id="prix"
            type="number"
            step="0.01"
            value={formData.prix}
            onChange={(e) => setFormData(prev => ({ ...prev, prix: e.target.value }))}
            placeholder="150.00"
          />
        </div>

        <div>
          <Label htmlFor="reference_articles">Référence des articles concernés</Label>
          <Input
            id="reference_articles"
            value={formData.reference_articles}
            onChange={(e) => setFormData(prev => ({ ...prev, reference_articles: e.target.value }))}
            placeholder="Filtre à huile Yamaha 12345"
          />
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Détails de la maintenance..."
            rows={3}
          />
        </div>

        {maintenanceToEdit && (
          <div>
            <Label htmlFor="statut">Statut</Label>
            <Select 
              value={formData.statut} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, statut: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[100]">
                {statuts.map((stat) => (
                  <SelectItem key={stat} value={stat}>{stat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label htmlFor="notifications" className="text-base">Rappels par email</Label>
            <p className="text-sm text-muted-foreground">
              Recevoir des rappels avant et après l'échéance
            </p>
          </div>
          <Switch
            id="notifications"
            checked={formData.notifications_enabled}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, notifications_enabled: checked }))}
          />
        </div>
      </div>

      <Button 
        type="submit" 
        className="w-full bg-nautic-gold hover:bg-nautic-lightgold text-nautic"
        disabled={isDisabled}
      >
        {isSubmitting ? 'Enregistrement...' : (maintenanceToEdit ? 'Modifier la maintenance' : 'Enregistrer la maintenance')}
      </Button>
    </form>
  );
};

export default FormulaireMaintenance;
